import { ELEMENT_UID_TAG, SLIDE_UID_TAG, createUid } from "./imUid";
import { isFullListShape } from "./fullListShape";
import { isReviewNoteShape } from "./reviewNoteShape";
import { toKind, type SlideShapeKind } from "./shapeKind";

/** 1 枚ぶんの確定結果。 */
export type ReconciledSlide = {
    /** アドインが発行した識別子。設定の鍵。 */
    uid: string;
    /** office.js の slide id。選択やジャンプに使う（鍵には使わない）。 */
    officeSlideId: string;
    index: number;
    shapes: ReconciledShape[];
};

export type ReconciledShape = {
    uid: string;
    /** office.js の shape id。選択やジャンプに使う（鍵には使わない）。 */
    officeShapeId: string;
    name: string;
    kind: SlideShapeKind;
    /**
     * 旧鍵。v7 までの設定はこれを鍵にしていた。
     *
     * **移行の対応表を作る以外に使わないこと。** 設定の鍵は `uid`。実装中、画面が
     * ここを鍵に取り違えて名前が旧鍵で保存される取りこぼしが起きた。
     * 対応表を作れるのは両方が揃うこの瞬間だけなので、持ち回り自体は必要。
     */
    creationId: string | null;
};

/** 走査でなにが起きたか。移行の記録と、白紙化の判断に使う。 */
export type ReconcileReport = {
    slides: ReconciledSlide[];
    /** 新しく識別子を振った件数（初見の枚・枠）。 */
    issued: number;
    /** 重複していたため振り直した件数。複製されたものはここに入る。 */
    reissued: number;
    /** 振り直した枠の識別子。設定を引き継がせないために、呼び出し側が使う。 */
    reissuedElementUids: string[];
    /** 振り直した枚の識別子。 */
    reissuedSlideUids: string[];
    /**
     * 旧鍵（`creationId`）を読めた環境か。PowerPointApi 1.10 非対応だと読めず、
     * `creationId` は全件 null になる。**false のとき旧鍵の対応表は作れない**ので、
     * 鍵の移行は見送ること（空の対応表で移行すると要素の設定が全消しになる）。
     */
    creationIdAvailable: boolean;
    /**
     * 発行した識別子をファイルへ**書けたか**。
     *
     * 読み取り専用で開いた pptx（SharePoint / OneDrive の文書、他の人が編集中のファイル）だと
     * タグの書き込みだけが失敗する。以前は読み書きを 1 つの処理として持っていたため、書き込みの
     * 失敗が「読めなかった」と同じ扱いになり、**読めていた紙面まで捨てて**画面が丸ごと使えなく
     * なっていた（記録 25）。識別子の発行はタグの無い図形があれば必ず起きるので、初めて開く
     * ファイルでは毎回失敗し、スライドを選び直しても回復しない。
     *
     * false のときは一覧を出してよいが、**設定の鍵の移行は見送る**。書けていない鍵へ設定を
     * 紐づけると、ファイルの中に無い鍵を指した設定が残る。
     */
    uidsPersisted: boolean;
};

/**
 * ファイル内の識別子を確定させる。欠けていれば発行し、重複していれば振り直す。
 *
 * 重複は先に現れた側を正とし、後続へ新しい値を振る。複製で生まれた枚・枠が後続に
 * なるため、複製側が新しい識別子を得る。設定は元の識別子に紐づいたままなので、
 * 複製側は白紙で始まる（2026-08-31 決定）。同じ担当に 2 枚が名指され続けるのを
 * 避けるための選択で、複製の主な用途が「似た構成の別スライドを作る」ことによる。
 *
 * **「複製が後続になる」前提は並べ替えで崩れる。** タスクペインを閉じたまま複製し、複製を元より
 * 前へ動かしてから開くと、正が複製側になって元の枚が白紙になる（記録 51）。ここで向きを直すのが
 * 本筋だが、**複製はタグごと写されるので、どちらが元かを見分ける手がかりがファイルの中に無い。**
 * 発行時刻を別タグに入れても複写され、`a16:creationId` も複製の元と先で同じで、設定は識別子に
 * 紐づくので「設定を持っている側」も両者で同じになる。したがって順序以外の判定材料が要る場合は、
 * 前回の走査で見た「識別子 → PowerPoint の slide id」を覚えておく必要がある（未実装）。
 * いまは振り直しの件数を返し、画面が起きたことを伝える。
 *
 * 1 往復で読み、書き込みが要るときだけ 2 往復目を使う。本番の 88 枚・1,170 図形で
 * 読みが 129ms だったため、初回だけ書き込みが乗る形なら実用範囲に収まる。
 *
 * **グループの中の図形まで下げる。** 最上位だけを見ていたため、実テンプレでは入れ子の中の
 * 270 個（文字入り 152 個）が枠一覧に出ず、対象にも選べなかった（記録 24）。組み立て側の
 * `_iter_shapes` は再帰するので、書き込む先としては成立している。中身は**グループの直後**へ
 * 並べ、識別子の重複を先に現れた側へ寄せる向きを紙面の並びと揃える。深さ 1 つあたり 2 往復
 * 増えるが、実物 10 本の入れ子の深さはすべて 1 だった。
 */
/**
 * グループを下げる深さの上限。
 *
 * 実物のフォーマット 10 本を数えたところ、入れ子の深さはすべて 1 だった（グループの中に
 * グループは無い）。深さ 1 つあたり 2 往復増えるので、際限なく下げない。上限に当たった分は
 * これまでどおり一覧に出ない。
 */
const MAX_GROUP_DEPTH = 4;

/** グループの中身を読める環境か。`Shape.group` は PowerPointApi 1.8。 */
function supportsGroupShapes(): boolean {
    try {
        return Office.context.requirements.isSetSupported("PowerPointApi", "1.8");
    } catch {
        return false;
    }
}

function isGroup(shape: PowerPoint.Shape): boolean {
    return shape.type === "Group";
}

/** 走査中の図形と、それが乗っている枚。控えの鍵に枚が要るので対で持ち回る。 */
type OwnedShape = { slideId: string; shape: PowerPoint.Shape };

/**
 * グループの中身の控えの鍵。
 *
 * **`Shape.id` はスライドの中でしか一意ではない。** 枚をまたぐと同じ id が再び現れる（実データ
 * では id 12 が 10 枚でグループとして使われ、同じ id の図形が全体で 16 個あった）。id だけで
 * 控えると、別の枚のグループの中身が結び付き、
 *
 * - その中身が枚を跨いで何度も辿られ、識別子の重複と判定されて振り直され続ける
 * - 画面Bが実体の無い枠を並べる（実測で 20 図形の枚に 113 行）
 *
 * という二つが起きる。枚の id と組にして分ける。区切りは id に現れ得ない NUL を使う。記号や
 * 空白だと、id 側にその文字が入った瞬間に別の組と同じ鍵になる。
 */
function childKey(slideId: string, shapeId: string): string {
    return `${slideId}\u0000${shapeId}`;
}

/**
 * グループの中身を 1 段だけ読み、枚と親の組ごとに控える。読めなければ `null`。
 *
 * **読めなくても走査全体を落とさない。** `Shape.group` は種別がグループでなければ
 * `GeneralException` を返す仕様で、版や図形の都合で落ちることがある。ここで投げると
 * 「紙面を読み取れませんでした」に潰れ、最上位まで含めて何も出せなくなる（記録 25 と同じ形）。
 */
async function expandGroups(
    ctx: PowerPoint.RequestContext,
    groups: OwnedShape[],
    withCreationId: boolean,
    childrenOf: Map<string, PowerPoint.Shape[]>
): Promise<OwnedShape[] | null> {
    for (const g of groups) {
        g.shape.group.shapes.load(
            withCreationId
                ? "items/id,items/name,items/type,items/creationId"
                : "items/id,items/name,items/type"
        );
    }
    try {
        await ctx.sync();
    } catch {
        return null;
    }

    const children: OwnedShape[] = [];
    for (const g of groups) {
        const items = g.shape.group.shapes.items;
        childrenOf.set(childKey(g.slideId, g.shape.id), items);
        for (const child of items) {
            child.tags.load("items/key,items/value");
            children.push({ slideId: g.slideId, shape: child });
        }
    }
    if (children.length === 0) return children;
    try {
        await ctx.sync();
    } catch {
        // 印は読めなかった。中身を一覧へ出すと識別子を持たない扱いになるので、控えを戻す。
        for (const g of groups) childrenOf.delete(childKey(g.slideId, g.shape.id));
        return null;
    }
    return children;
}

export async function reconcileUids(): Promise<ReconcileReport> {
    // creationId は PowerPointApi 1.10。移行の対応表にしか使わないので、非対応環境では諦める。
    const withCreationId = Office.context.requirements.isSetSupported("PowerPointApi", "1.10");
    return PowerPoint.run(async (ctx) => {
        const slides = ctx.presentation.slides;
        slides.load("items/id");
        await ctx.sync();

        for (const s of slides.items) {
            s.tags.load("items/key,items/value");
            s.shapes.load(
                withCreationId
                    ? "items/id,items/name,items/type,items/creationId"
                    : "items/id,items/name,items/type"
            );
        }
        await ctx.sync();

        for (const s of slides.items) {
            for (const sh of s.shapes.items) sh.tags.load("items/key,items/value");
        }
        await ctx.sync();

        // グループの中の図形まで下げる。実テンプレは入れ子の中に 270 個（文字入り 152 個）を
        // 持ち、本番の 9 本も 82〜126 個ある。最上位しか見ていなかったため、それらは枠一覧に
        // 出ず対象にも選べなかった（記録 24）。組み立て側の `_iter_shapes` は再帰するので、
        // 書き込む先としては成立している。
        const childrenOf = new Map<string, PowerPoint.Shape[]>();
        if (supportsGroupShapes()) {
            let frontier: OwnedShape[] = slides.items.flatMap((s) =>
                s.shapes.items.filter(isGroup).map((shape) => ({ slideId: s.id, shape }))
            );
            for (let depth = 0; frontier.length > 0 && depth < MAX_GROUP_DEPTH; depth += 1) {
                const expanded = await expandGroups(ctx, frontier, withCreationId, childrenOf);
                if (expanded === null) break; // 版や図形の都合で下げられない。読めた分で進む。
                frontier = expanded.filter((o) => isGroup(o.shape));
            }
        }

        const seenSlide: { [uid: string]: true } = {};
        const seenElement: { [uid: string]: true } = {};
        const report: ReconcileReport = {
            slides: [],
            issued: 0,
            reissued: 0,
            reissuedElementUids: [],
            reissuedSlideUids: [],
            creationIdAvailable: withCreationId,
            uidsPersisted: true,
        };
        let dirty = false;

        function pick(
            tags: PowerPoint.TagCollection,
            key: string,
            seen: { [uid: string]: true },
            prefix: "s" | "e"
        ): { uid: string; state: "kept" | "issued" | "reissued" } {
            const found = tags.items.filter((t) => t.key === key)[0];
            const current = found ? found.value : "";
            if (current.length === 0) {
                const uid = createUid(prefix);
                seen[uid] = true;
                tags.add(key, uid);
                return { uid, state: "issued" };
            }
            if (seen[current] === true) {
                const uid = createUid(prefix);
                seen[uid] = true;
                tags.add(key, uid);
                return { uid, state: "reissued" };
            }
            seen[current] = true;
            return { uid: current, state: "kept" };
        }

        let index = 0;
        for (const s of slides.items) {
            index += 1;
            // 生成が足す全量一覧の枚は、識別子も振らず一覧にも積まない。枚ごと外すので、表だけでなく
            // タイトルも書き込み先の候補に出ない。番号は数えるので、後ろの枚の「何枚目か」は変わらない。
            if (containsFullList(s.shapes.items, s.id)) continue;
            const su = pick(s.tags, SLIDE_UID_TAG, seenSlide, "s");
            if (su.state === "issued") report.issued += 1;
            if (su.state === "reissued") {
                report.reissued += 1;
                report.reissuedSlideUids.push(su.uid);
            }
            if (su.state !== "kept") dirty = true;

            const shapes: ReconciledShape[] = [];
            /**
             * 紙面の並び順に確定させる。**グループの直後にその中身を置く。**
             * 識別子の重複は先に現れた側を正とするので、順が変わると向きが変わる。
             */
            const visit = (sh: PowerPoint.Shape): void => {
                // 生成が置く要確認事項のボックスは、識別子も振らず一覧にも積まない。
                // 書き込み先の候補も名前の解決もこの結果から作られるので、入口で外せば全部から消える。
                if (isReviewNoteShape(sh)) return;
                const eu = pick(sh.tags, ELEMENT_UID_TAG, seenElement, "e");
                if (eu.state === "issued") report.issued += 1;
                if (eu.state === "reissued") {
                    report.reissued += 1;
                    report.reissuedElementUids.push(eu.uid);
                }
                if (eu.state !== "kept") dirty = true;
                shapes.push({
                    uid: eu.uid,
                    officeShapeId: sh.id,
                    name: sh.name,
                    kind: toKind(sh.type),
                    creationId: withCreationId ? (sh.creationId ?? null) : null,
                });
                for (const child of childrenOf.get(childKey(s.id, sh.id)) ?? []) visit(child);
            };
            for (const sh of s.shapes.items) visit(sh);

            report.slides.push({
                uid: su.uid,
                officeSlideId: s.id,
                index,
                shapes,
            });
        }

        /** 枚が全量一覧の表を持つか。グループの中まで見る。 */
        function containsFullList(items: PowerPoint.Shape[], slideId: string): boolean {
            return items.some(
                (sh) =>
                    isFullListShape(sh) ||
                    containsFullList(childrenOf.get(childKey(slideId, sh.id)) ?? [], slideId)
            );
        }

        // 読みと書きを分ける。**書き込みの失敗で読めた結果を捨てない**（記録 25）。
        if (dirty) {
            try {
                await ctx.sync();
            } catch {
                report.uidsPersisted = false;
            }
        }
        return report;
    });
}
